import { useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useReservationSettings } from "@/hooks/useReservationSettings";
import { useTimeSlotsQuery } from "@/hooks/queries/useTimeSlotsQuery";
import { useDateOverridesQuery } from "@/hooks/queries/useDateOverridesQuery";
import { useZoneTimeWindows } from "@/hooks/useZoneTimeWindows";
import { useBookableZones } from "@/hooks/useBookableZones";
import { getStaffSlotsForDate } from "@/lib/zoneScheduleUtils";
import { generateAvailableTimes } from "@/lib/timeBookingUtils";
import { formatDateLocal } from "@/lib/timeFormatUtils";
import { cn } from "@/lib/utils";
import { blockRangeFor, durationForTime, filterBlockedTimes, isTimeBlocked, slotLabelMinutes } from "../../supabase/functions/_shared/timeBlocks";

export interface OnlineClosureBlockRow {
  id: string;
  block_date: string;
  start_time: string;
  end_time: string;
  note: string | null;
  blocks_overlap: boolean;
}

export interface OnlineClosureEditTarget {
  kind: "from" | "hours";
  rows: OnlineClosureBlockRow[];
}

interface OnlineClosureDialogProps {
  establishmentId: string | null | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Date présélectionnée à l'ouverture (ex. date affichée au tableau de bord). */
  initialDate?: Date;
  /** Si fourni, la fenêtre s'ouvre en modification de ces lignes. */
  editTarget?: OnlineClosureEditTarget | null;
  /** Appelé après un enregistrement réussi (ex. recharger la liste). */
  onSaved?: () => void;
}

const HALF_HOURS = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);

/** Fenêtre « Fermeture en ligne » partagée entre la page Horaires et la vue du jour du tableau de bord. */
export function OnlineClosureDialog({ establishmentId, open, onOpenChange, initialDate, editTarget, onSaved }: OnlineClosureDialogProps) {
  const { toast } = useToast();
  const { session } = useAuth();
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState<Date | undefined>();
  const [kind, setKind] = useState<"from" | "hours">("from");
  const [fromTime, setFromTime] = useState("");
  const [hours, setHours] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const today = formatDateLocal(new Date());

  const { settings } = useReservationSettings({ establishmentId: establishmentId || undefined });
  const { data: timeSlots = [] } = useTimeSlotsQuery(establishmentId ?? null);
  const { data: overrides = [] } = useDateOverridesQuery(establishmentId ?? null, true);
  const { zoneWindows } = useZoneTimeWindows({ establishmentId: establishmentId || undefined, useAuthenticatedData: true });
  const { zones } = useBookableZones({ establishmentId: establishmentId || undefined, useAuthenticatedData: true });
  const activeZoneIds = useMemo(() => new Set(zones.filter(z => z.isActive).map(z => z.id)), [zones]);
  const bookingMode = settings?.bookingMode === "time" ? "time" : "slot";
  const interval = (settings?.bookingIntervalMinutes ?? 15) as 15 | 30 | 60;

  // Initialisation à chaque ouverture : modification pré-remplie, sinon date initiale éventuelle.
  useEffect(() => {
    if (!open) return;
    if (editTarget) {
      const rows = editTarget.rows;
      setDate(parseISO(rows[0].block_date));
      setKind(editTarget.kind);
      setFromTime(editTarget.kind === "from" ? rows[0].start_time.substring(0, 5) : "");
      setHours(editTarget.kind === "hours" ? rows.map(r => r.start_time.substring(0, 5)) : []);
      setNote(rows.find(r => r.note)?.note ?? "");
    } else {
      setDate(initialDate);
      setKind("from");
      setFromTime("");
      setHours([]);
      setNote("");
    }
  }, [open, editTarget, initialDate]);

  const dayTimes = useMemo(() => {
    if (!date) return [];
    const slots = getStaffSlotsForDate(date, timeSlots, overrides, zoneWindows, activeZoneIds);
    if (bookingMode === "time") {
      return generateAvailableTimes(
        slots.map(s => ({ start: s.start.substring(0, 5), end: s.end.substring(0, 5), durationOverrideMinutes: s.durationOverrideMinutes ?? null })),
        interval,
        settings?.reservationDurationMinutes ?? 120,
      );
    }
    return Array.from(new Set(slots.map(s => s.start.substring(0, 5))));
  }, [date, timeSlots, overrides, zoneWindows, activeZoneIds, bookingMode, interval, settings?.reservationDurationMinutes]);

  // Aperçu de l'impact d'une fermeture « à partir de » : dernière heure encore proposée aux clients,
  // calculée avec la même logique que les pages de réservation (durée réelle par créneau).
  const closureImpact = useMemo(() => {
    if (!date || kind !== "from" || !fromTime) return null;
    const dateStr = formatDateLocal(date);
    const slots = getStaffSlotsForDate(date, timeSlots, overrides, zoneWindows, activeZoneIds);
    if (slots.length === 0) return { state: "no-schedule" as const };
    const block = { block_date: dateStr, start_time: fromTime, end_time: "00:00", blocks_overlap: true };
    if (bookingMode === "time") {
      const ranges = slots.map(s => ({ start: s.start.substring(0, 5), end: s.end.substring(0, 5), durationOverrideMinutes: s.durationOverrideMinutes ?? null }));
      const remaining = filterBlockedTimes(
        dateStr,
        generateAvailableTimes(ranges, interval, settings?.reservationDurationMinutes ?? 120),
        [block],
        (t) => durationForTime(t, ranges, settings?.reservationDurationMinutes ?? 120),
      );
      if (remaining.length === 0) return { state: "none" as const };
      return { state: "ok" as const, lastStart: remaining[remaining.length - 1] };
    }
    // Mode créneaux : un créneau disparaît si sa durée déborde dans la fermeture (comme Book.tsx).
    const ordered: { start: string; dur: number }[] = [];
    slots.forEach(s => {
      const start = s.start.substring(0, 5);
      const dur = slotLabelMinutes(`${s.start} - ${s.end}`);
      const prev = ordered.find(o => o.start === start);
      if (prev) prev.dur = Math.max(prev.dur, dur);
      else ordered.push({ start, dur });
    });
    const remaining = ordered.filter(o => !isTimeBlocked(dateStr, o.start, [block], o.dur));
    if (remaining.length === 0) return { state: "none" as const };
    return { state: "ok" as const, lastStart: remaining[remaining.length - 1].start };
  }, [date, kind, fromTime, timeSlots, overrides, zoneWindows, activeZoneIds, bookingMode, interval, settings?.reservationDurationMinutes]);

  const save = async () => {
    if (!establishmentId || !date) return;
    const block_date = formatDateLocal(date);
    const trimmed = note.trim().slice(0, 200) || null;
    const base = { establishment_id: establishmentId, block_date, note: trimmed, created_by: session?.user?.id ?? null };
    const rows = kind === "from"
      ? [{ ...base, start_time: fromTime, end_time: "00:00", blocks_overlap: true }]
      : hours.map(h => ({ ...base, ...blockRangeFor(h, bookingMode === "time" ? interval : 1), blocks_overlap: false }));
    if (rows.length === 0 || (kind === "from" && !fromTime)) return;
    setSaving(true);
    let error: { code?: string } | null = null;
    if (editTarget && editTarget.kind === "from") {
      // Modification directe de la ligne unique « fermé dès … »
      const res = await supabase.from("time_slot_blocks").update({ block_date, start_time: fromTime, note: trimmed }).eq("id", editTarget.rows[0].id);
      error = res.error;
    } else if (editTarget) {
      // Heures précises : on remplace le groupe (les heures décochées rouvrent, les nouvelles se ferment)
      const del = await supabase.from("time_slot_blocks").delete().in("id", editTarget.rows.map(r => r.id));
      if (del.error) error = del.error;
      else {
        const ins = await supabase.from("time_slot_blocks").insert(rows);
        error = ins.error;
      }
    } else {
      const res = await supabase.from("time_slot_blocks").insert(rows);
      error = res.error;
    }
    setSaving(false);
    if (error) {
      console.error("Erreur enregistrement fermeture en ligne:", error);
      toast({
        variant: "destructive",
        title: "Impossible d'enregistrer",
        description: error.code === "23505" ? "Une fermeture existe déjà à cette date pour cette heure." : "Réessayez dans un instant.",
      });
      return;
    }
    toast({ title: editTarget ? "Fermeture modifiée" : "Fermeture enregistrée", description: "Les réservations existantes ne changent pas." });
    onOpenChange(false);
    onSaved?.();
  };

  const canSave = !!date && (kind === "from" ? !!fromTime : hours.length > 0) && !saving;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-1.5rem)] overflow-y-auto rounded-lg sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editTarget ? "Modifier la fermeture en ligne" : "Ajouter une fermeture en ligne"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Date</Label>
            <div className="mt-1 flex justify-center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => { setDate(d); setHours([]); }}
                disabled={(d) => formatDateLocal(d) < today}
                locale={fr}
                className="rounded-md border"
              />
            </div>
          </div>
          <div>
            <Label>Type</Label>
            <RadioGroup value={kind} onValueChange={(v) => setKind(v as "from" | "hours")} className="mt-2">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="from" id="oc-from" />
                <Label htmlFor="oc-from" className="font-normal">À partir d'une heure (aucune réservation ne peut commencer ni se prolonger après)</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="hours" id="oc-hours" />
                <Label htmlFor="oc-hours" className="font-normal">Heures précises</Label>
              </div>
            </RadioGroup>
          </div>
          {kind === "from" ? (
            <div>
              <Label htmlFor="oc-time">Heure</Label>
              <select
                id="oc-time"
                className="mt-1 h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={fromTime}
                onChange={e => setFromTime(e.target.value)}
              >
                <option value="">Choisir une heure</option>
                {HALF_HOURS.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
              {fromTime && closureImpact && (
                <div className="mt-2 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
                  <p>
                    Toute réservation en ligne doit se terminer au plus tard à{" "}
                    <span className="font-medium text-foreground">{fromTime}</span>.
                  </p>
                  {closureImpact.state === "no-schedule" ? (
                    <p className="mt-1">Aucun horaire ce jour-là : la fermeture n’aura aucun effet.</p>
                  ) : closureImpact.state === "none" ? (
                    <p className="mt-1">Aucune réservation en ligne ne sera possible ce jour-là.</p>
                  ) : (
                    <p className="mt-1">
                      {bookingMode === "time"
                        ? "Avec les durées actuelles, la dernière réservation en ligne pourra commencer à"
                        : "Le dernier créneau encore proposé sera"}{" "}
                      <span className="font-medium text-foreground">{closureImpact.lastStart}</span>.
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>
              <Label>Heures à fermer</Label>
              {!date ? (
                <p className="mt-1 text-sm text-muted-foreground">Choisissez d'abord une date.</p>
              ) : dayTimes.length === 0 ? (
                <p className="mt-1 text-sm text-muted-foreground">Aucun horaire ce jour-là.</p>
              ) : (
                <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {dayTimes.map(t => {
                    const on = hours.includes(t);
                    return (
                      <Button
                        key={t}
                        type="button"
                        size="sm"
                        variant={on ? "destructive" : "outline"}
                        aria-pressed={on}
                        className={cn("h-10", on && "line-through")}
                        onClick={() => setHours(on ? hours.filter(h => h !== t) : [...hours, t])}
                      >
                      {t}
                    </Button>
                  );
                })}
                </div>
              )}
              {date && dayTimes.length > 0 && (
                <p className="mt-2 text-xs text-muted-foreground">Seules ces heures précises disparaissent ; une réservation déjà commencée n’est pas touchée.</p>
              )}
            </div>
          )}
          <div>
            <Label htmlFor="oc-note">Note interne (facultatif)</Label>
            <Textarea id="oc-note" maxLength={200} value={note} onChange={e => setNote(e.target.value)} placeholder="Ex. Privatisation, gérée par e-mail" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={!canSave}>{saving ? "Enregistrement…" : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
